import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  LayoutDashboard,
  Package,
  ShoppingCart,
  Truck,
  Users,
  Settings,
  TrendingUp,
  Shield,
  Wallet,
  Award,
  Megaphone,
  History,
  ClipboardList,
  Search,
  FileText,
  User,
  Building2,
  Loader2
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const GlobalSearch = ({ open, setOpen }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState({
    orders: [],
    leads: [],
    vendors: [],
    inventory: [],
    purchaseOrders: []
  });
  const [loading, setLoading] = useState(false);

  // Check if user is admin (both is_admin flag and legacy roles check)
  const isAdmin = user?.is_admin || user?.roles?.includes('admin');

  // Navigation items for quick access
  const navigationItems = [
    { name: 'Dashboard', icon: LayoutDashboard, path: '/dashboard', keywords: ['home', 'overview', 'stats'] },
    { name: 'Orders', icon: ShoppingCart, path: '/orders', keywords: ['sales', 'shopify', 'fulfillment'] },
    { name: 'Leads & Sales', icon: TrendingUp, path: '/leads', keywords: ['crm', 'prospects', 'quotes'] },
    { name: 'Inventory', icon: Package, path: '/inventory', keywords: ['stock', 'products', 'items'] },
    { name: 'Vendors', icon: Building2, path: '/vendors', keywords: ['suppliers', 'partners'] },
    { name: 'Purchase Orders', icon: ClipboardList, path: '/purchase-orders', keywords: ['po', 'procurement'] },
    { name: 'Deliveries', icon: Truck, path: '/deliveries', keywords: ['shipping', 'fulfillment', 'drivers'] },
    { name: 'Vendor Pickups', icon: Package, path: '/vendor-pickups', keywords: ['collection', 'pickup'] },
    { name: 'Tasks', icon: ClipboardList, path: '/tasks', keywords: ['todo', 'work'] },
    { name: 'Cash Management', icon: Wallet, path: '/cash-management', keywords: ['money', 'payments', 'cod'] },
    { name: 'Incentives', icon: Award, path: '/incentives', keywords: ['bonus', 'rewards'] },
    { name: 'Announcements', icon: Megaphone, path: '/announcements', keywords: ['news', 'notifications'] },
    { name: 'User Management', icon: Users, path: '/users', keywords: ['team', 'staff', 'employees'], adminOnly: true },
    { name: 'Role Management', icon: Shield, path: '/roles', keywords: ['permissions', 'access'], adminOnly: true },
    { name: 'Audit Log', icon: History, path: '/audit-log', keywords: ['history', 'activity', 'logs'], adminOnly: true },
    { name: 'Settings', icon: Settings, path: '/settings', keywords: ['config', 'preferences', 'mamopay', 'shopify', 'zoho'] },
  ];

  // Filter navigation items based on search
  const filteredNavItems = navigationItems.filter(item => {
    if (item.adminOnly && !isAdmin) return false;
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      item.name.toLowerCase().includes(query) ||
      item.keywords.some(k => k.includes(query))
    );
  });

  // Search backend for entities
  const searchEntities = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSearchResults({ orders: [], leads: [], vendors: [], inventory: [], purchaseOrders: [] });
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Search multiple endpoints in parallel
      const [ordersRes, leadsRes, vendorsRes, inventoryRes, poRes] = await Promise.allSettled([
        axios.get(`${API_BASE_URL}/orders?search=${encodeURIComponent(query)}&limit=5`, { headers }),
        axios.get(`${API_BASE_URL}/leads?search=${encodeURIComponent(query)}&limit=5`, { headers }),
        axios.get(`${API_BASE_URL}/vendors?search=${encodeURIComponent(query)}&limit=5`, { headers }),
        axios.get(`${API_BASE_URL}/inventory?search=${encodeURIComponent(query)}&limit=5`, { headers }),
        axios.get(`${API_BASE_URL}/purchase-orders?search=${encodeURIComponent(query)}&limit=5`, { headers }),
      ]);

      setSearchResults({
        orders: ordersRes.status === 'fulfilled' ? (ordersRes.value.data.orders || ordersRes.value.data || []).slice(0, 5) : [],
        leads: leadsRes.status === 'fulfilled' ? (leadsRes.value.data.leads || leadsRes.value.data || []).slice(0, 5) : [],
        vendors: vendorsRes.status === 'fulfilled' ? (vendorsRes.value.data.vendors || vendorsRes.value.data || []).slice(0, 5) : [],
        inventory: inventoryRes.status === 'fulfilled' ? (inventoryRes.value.data || []).slice(0, 5) : [],
        purchaseOrders: poRes.status === 'fulfilled' ? (poRes.value.data.purchase_orders || poRes.value.data || []).slice(0, 5) : [],
      });
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.length >= 2) {
        searchEntities(searchQuery);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, searchEntities]);

  // Keyboard shortcut to open search
  useEffect(() => {
    const down = (e) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, [setOpen]);

  const handleSelect = (path) => {
    setOpen(false);
    setSearchQuery('');
    navigate(path);
  };

  const hasSearchResults = 
    searchResults.orders.length > 0 ||
    searchResults.leads.length > 0 ||
    searchResults.vendors.length > 0 ||
    searchResults.inventory.length > 0 ||
    searchResults.purchaseOrders.length > 0;

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <Command className="bg-wt-surface border-wt-border">
        <CommandInput 
          placeholder="Search orders, vendors, leads, settings..." 
          value={searchQuery}
          onValueChange={setSearchQuery}
          className="text-wt-text-primary"
        />
        <CommandList className="max-h-[400px]">
          {loading && (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-6 w-6 animate-spin text-wt-gold" />
            </div>
          )}

          {!loading && searchQuery.length >= 2 && !hasSearchResults && filteredNavItems.length === 0 && (
            <CommandEmpty className="text-wt-text-muted">No results found.</CommandEmpty>
          )}

          {/* Orders Results */}
          {searchResults.orders.length > 0 && (
            <CommandGroup heading="Orders" className="text-wt-text-muted">
              {searchResults.orders.map((order) => (
                <CommandItem
                  key={order.id}
                  value={`order-${order.id}`}
                  onSelect={() => handleSelect(`/orders?view=${order.id}`)}
                  className="text-wt-text-primary hover:bg-wt-subtle cursor-pointer"
                >
                  <ShoppingCart className="mr-2 h-4 w-4 text-wt-gold" />
                  <div className="flex flex-col">
                    <span>{order.order_number || order.id}</span>
                    <span className="text-xs text-wt-text-muted">
                      {order.customer_name} • {order.fulfillment_status}
                    </span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          {/* Leads Results */}
          {searchResults.leads.length > 0 && (
            <CommandGroup heading="Leads" className="text-wt-text-muted">
              {searchResults.leads.map((lead) => (
                <CommandItem
                  key={lead.id}
                  value={`lead-${lead.id}`}
                  onSelect={() => handleSelect(`/leads/${lead.id}`)}
                  className="text-wt-text-primary hover:bg-wt-subtle cursor-pointer"
                >
                  <TrendingUp className="mr-2 h-4 w-4 text-purple-400" />
                  <div className="flex flex-col">
                    <span>{lead.name}</span>
                    <span className="text-xs text-wt-text-muted">
                      {lead.email || lead.phone} • {lead.stage}
                    </span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          {/* Vendors Results */}
          {searchResults.vendors.length > 0 && (
            <CommandGroup heading="Vendors" className="text-wt-text-muted">
              {searchResults.vendors.map((vendor) => (
                <CommandItem
                  key={vendor.id}
                  value={`vendor-${vendor.id}`}
                  onSelect={() => handleSelect(`/vendors/${vendor.id}`)}
                  className="text-wt-text-primary hover:bg-wt-subtle cursor-pointer"
                >
                  <Building2 className="mr-2 h-4 w-4 text-blue-400" />
                  <div className="flex flex-col">
                    <span>{vendor.name}</span>
                    <span className="text-xs text-wt-text-muted">
                      {vendor.category} • {vendor.contact_person}
                    </span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          {/* Inventory Results */}
          {searchResults.inventory.length > 0 && (
            <CommandGroup heading="Inventory" className="text-wt-text-muted">
              {searchResults.inventory.map((item) => (
                <CommandItem
                  key={item.id}
                  value={`inventory-${item.id}`}
                  onSelect={() => handleSelect(`/inventory?search=${encodeURIComponent(item.product_name || item.sku)}`)}
                  className="text-wt-text-primary hover:bg-wt-subtle cursor-pointer"
                >
                  <Package className="mr-2 h-4 w-4 text-green-400" />
                  <div className="flex flex-col">
                    <span>{item.product_name || item.sku}</span>
                    <span className="text-xs text-wt-text-muted">
                      SKU: {item.sku} • Stock: {item.quantity}
                    </span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          {/* Purchase Orders Results */}
          {searchResults.purchaseOrders.length > 0 && (
            <CommandGroup heading="Purchase Orders" className="text-wt-text-muted">
              {searchResults.purchaseOrders.map((po) => (
                <CommandItem
                  key={po.id}
                  value={`po-${po.id}`}
                  onSelect={() => handleSelect(`/purchase-orders?view=${po.id}`)}
                  className="text-wt-text-primary hover:bg-wt-subtle cursor-pointer"
                >
                  <ClipboardList className="mr-2 h-4 w-4 text-orange-400" />
                  <div className="flex flex-col">
                    <span>{po.po_number || po.id}</span>
                    <span className="text-xs text-wt-text-muted">
                      {po.vendor_name} • {po.status}
                    </span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          {(hasSearchResults || searchQuery.length >= 2) && filteredNavItems.length > 0 && (
            <CommandSeparator className="bg-wt-border" />
          )}

          {/* Navigation / Pages */}
          {filteredNavItems.length > 0 && (
            <CommandGroup heading="Pages" className="text-wt-text-muted">
              {filteredNavItems.slice(0, searchQuery ? 10 : 6).map((item) => (
                <CommandItem
                  key={item.path}
                  value={item.name}
                  onSelect={() => handleSelect(item.path)}
                  className="text-wt-text-primary hover:bg-wt-subtle cursor-pointer"
                >
                  <item.icon className="mr-2 h-4 w-4 text-wt-text-muted" />
                  <span>{item.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
        </CommandList>

        {/* Footer with keyboard hint */}
        <div className="border-t border-wt-border p-2 text-xs text-wt-text-muted flex items-center justify-between">
          <span>Type to search across orders, leads, vendors...</span>
          <kbd className="px-1.5 py-0.5 bg-wt-subtle rounded text-xs">ESC to close</kbd>
        </div>
      </Command>
    </CommandDialog>
  );
};

export default GlobalSearch;
